import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../Utils/axiosConfig";
import { useAppDispatch, useAppSelector } from "../../../Hooks/useStore";
import { setIsLogin } from "./checkLoginSlice";
import { alert } from "../../../Utils/toast";
import { CheckLoginDataTypes } from "../../../Schema/checkLogin.types";
import { getCurrentLanguage } from "../../../Utils/sagaHelpers";
import i18next from "../../../i18next";
import { call } from "@redux-saga/core/effects";
import { queryClient } from "../../store";

export type CheckLoginLoadAction = {
  type: string;
  payload: null;
};

export type CheckLoginData = {
  data: CheckLoginDataTypes;
};

const CheckLogin = new ReduxFetchState<CheckLoginData, {}, any>("checkLogin");

export function* watchCheckLogin(action: CheckLoginLoadAction) {
  try {
    const lang: "fa" | "en" = yield getCurrentLanguage();

    const data: CheckLoginData = yield call([queryClient, queryClient.fetchQuery], {
      queryKey: ["checkLogin", lang],
      queryFn: async () => {
        const res = await axios({
          url: "/api/v1/users/ai/check-login",
          method: "GET",
          headers: { lang },
        });

        return { data: res.data };
      },
      staleTime: 24 * 60 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
    });

    if (data.data.user.type === "permanent") {
      yield put(setIsLogin(true));
    } else {
      yield put(setIsLogin(false));
    }

    yield put(CheckLogin.actions.loadSuccess(data));
  } catch (e: any) {
    if (e.code === "ECONNABORTED") {
      //@ts-ignore
      alert({ message: i18next.t("failedToFetch"), type: "error" });
    } else {
      alert({
        //@ts-ignore
        message: e?.response?.data?.error ?? i18next.t("somethingWentWrong"),
        type: "error",
      });
    }

    yield put(setIsLogin(false));
    yield put(CheckLogin.actions.loadFailure(e.message));
  }
}

export function* checkLoginSagas() {
  yield takeEvery(CheckLogin.actionTypes.load, watchCheckLogin);
}

export function useCheckLogin() {
  const { loading, data, ...checkLoginState } = useAppSelector((state) => state.checkLogin);
  const dispatch = useAppDispatch();

  const dispatchCheckLogin = useCallback(() => {
    dispatch(CheckLogin.actions.load());
  }, [dispatch]);

  return {
    ...checkLoginState,
    dispatchCheckLogin,
    checkLoginLoading: loading,
    checkLoginData: data,
  };
}

export const {
  actionTypes: checkLoginActionsTypes,
  actions: checkLoginActions,
  reducer: checkLoginReducer,
} = CheckLogin;
